import * as XLSX from 'xlsx-js-style';

interface ICentralWarehouseStock {
  id: number;
  name: string;
  trade_name: string;
  actual_quantity: number;
  average_daily_output: number;
  days_in_stock: number;
}

interface IGeneralAnalysis {
  id: number;
  name: string;
  analyzed_period: number;
  number_of_supplements: number;
  total_consumption_period: number;
  supplemented_animals: number;
  average_weight_of_supplemented_animals: number;
  total_weight_of_supplemented_animals: number;
  uas_total_number_of_animals_supplemented: number;
  average_consumption_in_the_period_kg: number;
  average_consumption_in_the_period_ua: number;
  replacement_frequency: number;
}

interface IDepositInPasture {
  micro_area_id: number;
  initials: string;
  farm_general_inventory_id: number;
  product_type: string;
  trade_name: string;
  name: string;
  stock_balance: number;
  stock_capacity: number;
}

export const exportSupplementationInventoryToExcel = (
  centralWarehouse: ICentralWarehouseStock[],
  generalAnalysis: IGeneralAnalysis[],
  depositeInPasture: IDepositInPasture[],
  startDate?: Date,
  endDate?: Date
) => {
  if (!centralWarehouse || !generalAnalysis || !depositeInPasture) {
    throw new Error('Dados insuficientes para exportação');
  }

  const workbook = XLSX.utils.book_new();

  const warehouseData = centralWarehouse.map((item) => ({
    'Inner de Suplementação': item.name,
    'Produto': item.trade_name,
    'Estoque total (Kg)': item.actual_quantity,
    'Média de saídas diárias (Kg/dia)': item.average_daily_output,
    'Dias em estoque (dias)': item.days_in_stock
  }));

  if (warehouseData.length > 0) {
    const warehouseWorksheet = XLSX.utils.json_to_sheet(warehouseData);
    warehouseWorksheet['!cols'] = [
      { wch: 30 }, { wch: 25 }, { wch: 20 }, { wch: 25 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(workbook, warehouseWorksheet, 'Depósito Central');
  }

  const analysisData = generalAnalysis.map((item) => ({
    'Classificação do Produto': item.name,
    'Consumo Total no Período (Kg)': item.total_consumption_period,
    'Período Analisado (dias)': item.analyzed_period,
    'Nº Médio de Animais Suplementados': item.supplemented_animals,
    'Peso Médio dos Animais (Kg)': parseFloat(item.average_weight_of_supplemented_animals.toFixed(2)),
    'Peso Total dos Animais (Kg)': parseFloat(item.total_weight_of_supplemented_animals.toFixed(2)),
    'Total de UA': parseFloat(item.uas_total_number_of_animals_supplemented.toFixed(2)),
    'Consumo Médio (g/kgPV.dia)': parseFloat(item.average_consumption_in_the_period_kg.toFixed(3)),
    'Consumo Médio (g/UA.dia)': parseFloat(item.average_consumption_in_the_period_ua.toFixed(3)),
    'Frequência de Reposição (dias)': parseFloat(item.replacement_frequency.toFixed(2))
  }));

  if (analysisData.length > 0) {
    const analysisWorksheet = XLSX.utils.json_to_sheet(analysisData);
    analysisWorksheet['!cols'] = [
      { wch: 30 }, { wch: 25 }, { wch: 20 }, { wch: 25 }, { wch: 25 },
      { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 25 }
    ];
    XLSX.utils.book_append_sheet(workbook, analysisWorksheet, 'Análise Geral');
  }

  const pastureData = depositeInPasture.map((item) => {
    const stockBalance = item.stock_balance != null && typeof item.stock_balance === 'number' ? item.stock_balance : null;
    const stockCapacity = item.stock_capacity != null && typeof item.stock_capacity === 'number' ? item.stock_capacity : null;
    const percentage = stockCapacity != null && stockCapacity !== 0 && stockBalance != null ? 
      (stockBalance / stockCapacity * 100) : 0;
    
    return {
      'Pasto': item.initials,
      'Tipo de Suplemento': item.name,
      'Quantidade no Depósito (Kg)': stockBalance != null ? stockBalance : '-',
      'Capacidade Total (Kg)': stockCapacity != null ? stockCapacity : 0,
      '% Capacidade Ocupada': percentage > 0 ? `${parseFloat(percentage.toFixed(2))}%` : '0%',
      '_percentageValue': percentage
    };
  });

  const totalBalance = depositeInPasture.reduce((acc, item) => {
    const balance = item.stock_balance != null && typeof item.stock_balance === 'number' ? item.stock_balance : 0;
    return acc + balance;
  }, 0);
  const totalCapacity = depositeInPasture.reduce((acc, item) => {
    const capacity = item.stock_capacity != null && typeof item.stock_capacity === 'number' ? item.stock_capacity : 0;
    return acc + capacity;
  }, 0);
  const totalPercentage = totalCapacity !== 0 && totalBalance != null ? (totalBalance / totalCapacity * 100) : 0;
  const totalPercent = totalCapacity !== 0 && totalBalance != null ? `${parseFloat(totalPercentage.toFixed(2))}%` : '0%';

  pastureData.push({
    'Pasto': 'Total geral',
    'Tipo de Suplemento': '',
    'Quantidade no Depósito (Kg)': totalBalance,
    'Capacidade Total (Kg)': totalCapacity,
    '% Capacidade Ocupada': totalPercent,
    '_percentageValue': totalPercentage
  });

  if (pastureData.length > 1) {
    const pastureDataForSheet = pastureData.map(({ _percentageValue, ...rest }) => rest);
    const pastureWorksheet = XLSX.utils.json_to_sheet(pastureDataForSheet);
    
    const range = XLSX.utils.decode_range(pastureWorksheet['!ref'] || 'A1');
    const percentageColIndex = 4;
    
    const getColorByPercentage = (percentage: number): string => {
      if (percentage <= 30) {
        return 'FF0000';
      } else if (percentage >= 31 && percentage <= 50) {
        return 'FFFF00';
      } else {
        return '00FF00';
      }
    };

    for (let row = 1; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: percentageColIndex });
      
      if (!pastureWorksheet[cellAddress]) {
        pastureWorksheet[cellAddress] = { v: '', t: 's' };
      }
      
      const dataIndex = row - 1;
      const percentage = pastureData[dataIndex]?._percentageValue ?? 0;
      const color = getColorByPercentage(percentage);
      
      if (!pastureWorksheet[cellAddress].s) {
        pastureWorksheet[cellAddress].s = {};
      }
      pastureWorksheet[cellAddress].s.fill = {
        fgColor: { rgb: color }
      };
    }

    const lastRow = range.e.r + 1;
    const legendStartRow = lastRow + 2;
    
    const legendHeaderCell = XLSX.utils.encode_cell({ r: legendStartRow, c: 0 });
    pastureWorksheet[legendHeaderCell] = { v: 'Legenda', t: 's' };
    pastureWorksheet[legendHeaderCell].s = {
      font: { bold: true },
      alignment: { horizontal: 'left' }
    };

    const legendValues = ['<=30%', '>=31% <=50%', '>50%'];
    const legendColors = ['FF0000', 'FFFF00', '00FF00'];
    
    legendValues.forEach((value, index) => {
      const legendCell = XLSX.utils.encode_cell({ r: legendStartRow, c: index + 1 });
      pastureWorksheet[legendCell] = { v: value, t: 's' };
    });

    legendColors.forEach((color, index) => {
      const exampleCell = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: index + 1 });
      pastureWorksheet[exampleCell] = { v: '', t: 's' };
      pastureWorksheet[exampleCell].s = {
        fill: { fgColor: { rgb: color } }
      };
    });

    const exampleHeaderCell = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: 0 });
    pastureWorksheet[exampleHeaderCell] = { v: '', t: 's' };

    pastureWorksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: legendStartRow + 1, c: 4 }
    });

    pastureWorksheet['!cols'] = [
      { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(workbook, pastureWorksheet, 'Depósito Pasto');
  }

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  let filename = 'suplementacao-estoque';
  if (startDate && endDate) {
    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];
    filename = `suplementacao-estoque-${startStr}-${endStr}.xlsx`;
  } else {
    filename = `suplementacao-estoque-${new Date().toISOString().split('T')[0]}.xlsx`;
  }
  
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};




